import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import serverURL from '../config';
import CandidatesTable from './CandidatesTable';
import logo from '../assets/logo.svg';
import Header from './Header';
import Footer from './Footer';

const ConsolidatedReport = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [report, setReport] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [failedCandidates, setFailedCandidates] = useState([]);
    const [validCandidates, setValidCandidates] = useState([]);
    const [totalQuestions, setTotalQuestions] = useState(25);
    const [passThreshold, setPassThreshold] = useState(23);

    useEffect(() => {
        const fetchReport = async () => {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${serverURL}/reports/consolidated/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Extract total_questions and candidates from response
            const totalQs = response.data.total_questions || 25;
            const candidates = response.data.candidates || response.data;

            setTotalQuestions(totalQs);
            // Calculate 92% threshold, rounded up
            const threshold = Math.ceil(totalQs * 0.92);
            setPassThreshold(threshold);

            setReport(Array.isArray(candidates) ? candidates : []);
        };

        fetchReport();
    }, [id]);

    // Sort report by Total Correct + Programming Points
    const sortedReport = report.sort((a, b) => {
        const aScore = parseInt(a.total_correct) + parseInt(a.programming_points);
        const bScore = parseInt(b.total_correct) + parseInt(b.programming_points);
        return bScore - aScore;
    });

    // Filter sortedReport based on search term
    const filteredReport = sortedReport.filter(item =>
        item.candidate_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.candidate_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.candidate_id.toString().includes(searchTerm)
    );

    const handleExport = () => {
        const csvRows = [];
        csvRows.push(['Candidate ID', 'Candidate Name', 'Email', 'Total Correct', 'Total Attempted', 'Programming Points']);
        filteredReport.forEach(item => {
            csvRows.push([
                item.candidate_id,
                item.candidate_name,
                item.candidate_email,
                item.total_correct,
                item.total_attempted,
                item.programming_points,
            ]);
        });

        const csvString = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'consolidated_report.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    // Calculate failed candidates and filter for vthink domain
    const calculateFailedCandidates = () => {
        const failed = filteredReport.filter(candidate => {
            const totalAttempted = parseInt(candidate.total_attempted) || 1; // Avoid division by zero
            const totalCorrect = parseInt(candidate.total_correct);
            const percentage = (totalCorrect / totalAttempted) * 100;
            return percentage < 92; // Below 92% pass threshold
        });

        const valid = failed.filter(candidate =>
            candidate.candidate_email.endsWith('@vthink.co.in')
        );

        setFailedCandidates(failed);
        setValidCandidates(valid);
        setShowEmailModal(true);
    };

    // Handle sending retest emails
    const handleSendRetestEmails = async () => {
        if (validCandidates.length === 0) {
            toast.warning('No valid candidates to send emails to');
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const candidateIds = validCandidates.map(c => c.candidate_id);

            const response = await axios.post(
                `${serverURL}/notifications/send-retest/${id}`,
                { candidateIds },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                toast.success(`Successfully sent ${response.data.data.sent} retest emails!`);
                setShowEmailModal(false);
            } else {
                toast.error('Failed to send emails');
            }
        } catch (error) {
            console.error('Error sending emails:', error);
            toast.error(error.response?.data?.message || 'Error sending retest emails');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-100">
            <Header headerTitle={"Consolidated Report"} />

            <main className="flex-grow p-6">
                <input
                    type="text"
                    placeholder="Search by name, email, or ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border p-2 mb-4 w-full"
                />
                <div className='flex items-center justify-between mb-4'>
                    <div>
                        <p>Total Candidates: {filteredReport.length}</p>
                    </div>
                    <div className="flex gap-2">
                        {filteredReport.some(c => {
                            const totalAttempted = parseInt(c.total_attempted) || 1;
                            const totalCorrect = parseInt(c.total_correct);
                            const percentage = (totalCorrect / totalAttempted) * 100;
                            return percentage < 92 && c.candidate_email.endsWith('@vthink.co.in');
                        }) && (
                                <button
                                    onClick={calculateFailedCandidates}
                                    className="bg-red-500 text-white p-2 rounded hover:bg-red-600 transition-colors"
                                >
                                    Send Retest Emails
                                </button>
                            )}
                        <button
                            onClick={handleExport}
                            className="bg-gray-600 text-white p-2 rounded hover:bg-gray-700 transition-colors"
                        >
                            Export as CSV
                        </button>
                    </div>
                </div>
                {filteredReport.length > 0 ? (
                    <CandidatesTable candidates={filteredReport} passThreshold={passThreshold} />
                ) : (
                    <p className="text-lg">No report data available.</p>
                )}
            </main>

            {/* Email Modal */}
            {showEmailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h2 className="text-2xl font-bold mb-4 text-gray-800">Send Retest Emails</h2>
                        <div className="bg-gray-50 p-4 rounded mb-4 text-sm">
                            <p className="mb-2"><span className="font-semibold">Pass Threshold:</span> 92% (minimum passing percentage)</p>
                            <p className="mb-2"><span className="font-semibold">Total Failed:</span> {failedCandidates.length} (percentage &lt; 92%)</p>
                            <p className="mb-2"><span className="font-semibold">Valid (@vthink.co.in):</span> <span className="text-green-600 font-semibold">{validCandidates.length}</span></p>
                            <p className="text-red-600"><span className="font-semibold">Will be skipped:</span> {failedCandidates.length - validCandidates.length}</p>
                        </div>
                        <p className="text-gray-700 mb-4 text-sm">
                            Retest notification emails will be sent to {validCandidates.length} candidates from VThink domain for the same event and test.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowEmailModal(false)}
                                disabled={isLoading}
                                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSendRetestEmails}
                                disabled={isLoading || validCandidates.length === 0}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 'Sending...' : 'Send Emails'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default ConsolidatedReport; 